import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { IGeoJSONCircle, IUbicacionBase } from 'modelos/src';
import { retry, Subscription, throwError, timer } from 'rxjs';
import { AuxiliaresModule } from '../../../../../../auxiliares/auxiliares.module';
import { HelperService } from '../../../../../../auxiliares/servicios/helper.service';
import { Lista2Service } from '../../../../../../auxiliares/servicios/lista2.service';
import { ClienteJerarquiaService } from '../../../../../../auxiliares/servicios/cliente-jerarquia.service';
import {
  MapCardComponent,
  MapCardConfig,
} from '../../../../../../componentes/map-card/map-card.component';
import { PIEZAS_DETALLE } from '../../../../../../componentes/detalle/detalle.components';
import { CrearEditarCentroAtencionComponent } from '../crear-editar-centro-atencion/crear-editar-centro-atencion.component';
import { FormularioDialogService } from '../../../../../../auxiliares/servicios/formulario-dialog.service';

type ICentro = IUbicacionBase<'Centro de Atención'>;

@Component({
  selector: 'app-detalles-centro-atencion',
  imports: [AuxiliaresModule, MapCardComponent, ...PIEZAS_DETALLE],
  templateUrl: './detalles-centro-atencion.component.html',
  styleUrls: [
    '../../../../../../componentes/detalle/detalle-pagina.scss',
    '../../detalle-emergencias.scss',
  ],
})
export class DetallesCentroAtencionComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private formularios = inject(FormularioDialogService);
  private lista2 = inject(Lista2Service);
  private clienteJerarquia = inject(ClienteJerarquiaService);
  helper = inject(HelperService);

  public loading = true;
  public id = '';
  public centro?: ICentro;
  private centro$?: Subscription;
  private params$?: Subscription;

  public mapCardConfig?: MapCardConfig;
  /**
   * El cliente, sin HTML. Se calcula al dibujar: el índice de clientes de la
   * jerarquía puede llegar después que la entidad (al recargar la página el
   * chip quedaba vacío).
   */
  get cliente(): string | undefined {
    if (HelperService.getTipoCliente() === 'Final') return undefined;
    const e = this.centro;
    const cadena = this.clienteJerarquia.cadena(e?.cliente ?? e?.idCliente);
    return cadena[cadena.length - 1]?.nombre || e?.cliente?.nombre || undefined;
  }
  public fechaAlta?: string;
  public radio?: string;

  public puedeEditar = this.helper.tienePermiso(
    'Emergencias Médicas - Editar centros médicos',
  );

  private listar(): void {
    this.centro$?.unsubscribe();
    this.centro$ = this.lista2
      .ubicacion<'Centro de Atención'>(this.id, 30)
      // Un request que falla terminaba el refresco de cada 30 s para siempre.
      // Si todavía no se cargó nunca, el error sigue de largo.
      .pipe(
        retry({
          delay: (error) =>
            this.centro ? timer(30000) : throwError(() => error),
        }),
      )
      .subscribe({
        next: (data) => {
          if (!HelperService.cambiaronLosDatos(this.centro, data)) return;
          this.centro = data as ICentro;
          this.armar();
          this.loading = false;
        },
        error: (error) => {
          console.error(error);
          this.helper.notifError(error);
          // No existe o no hay acceso: sin esto quedaba el spinner para siempre
          this.loading = false;
        },
      });
  }

  private armar(): void {
    const c = this.centro;
    if (!c) return;
    const circulo = c.geojson as IGeoJSONCircle | undefined;
    this.radio = circulo?.radius
      ? `${Math.round(circulo.radius).toLocaleString('es-AR')} m`
      : undefined;
    this.fechaAlta = c.fechaCreacion
      ? HelperService.getFechaYHora(c.fechaCreacion)
      : undefined;

    const coor = circulo?.coordinates;
    this.mapCardConfig = coor
      ? {
          title: 'Ubicación',
          subtitle: HelperService.extraerDireccion(c.direccion ?? ''),
          icon: 'location_on',
          color: 'red',
          mapOptions: {
            markers: [
              {
                label: 'centroMedico',
                coor,
                id: c._id,
                centrar: true,
                data: c,
              },
            ],
            center: coor,
            zoom: 15,
          },
        }
      : undefined;
  }

  /** Edita en diálogo; al guardar se recarga el detalle. */
  public editar(): void {
    const ref = this.formularios.abrir(CrearEditarCentroAtencionComponent, {
      tamano: 'mediano',
      data: { id: this.id },
    });
    ref.afterClosed().subscribe((guardado) => {
      if (guardado) this.listar();
    });
  }

  ngOnInit(): void {
    // Al cambiar el id de la ruta Angular reutiliza el componente
    this.params$ = this.route.paramMap.subscribe((params) => {
      this.id = params.get('id') ?? '';
      this.loading = true;
      this.centro = undefined;
      this.listar();
    });
  }

  ngOnDestroy(): void {
    this.centro$?.unsubscribe();
    this.params$?.unsubscribe();
  }
}
